import { useQuery } from '@tanstack/react-query'
import { AuthService } from '@/shared/api/rest/services/auth.service'
import {
    ParentDuplicateRecord,
    ParentDuplicatesService
} from '@/shared/api/rest/services/parent-duplicates.service'

/**
 * Shared cache entries. Every consumer — the admin registry, the parent review
 * page, the notification feed — reads through these keys so a decision taken
 * in one place refreshes all of them.
 */
export const DUPLICATES_PENDING_KEY = ['parent-duplicates', 'pending']
export const DUPLICATES_PROCESSED_KEY = ['parent-duplicates', 'processed']
export const DUPLICATES_MINE_KEY = ['parent-duplicates', 'mine']

/** Cache key for "my parent record", shared with the party hydration hook. */
export const PARENT_ME_KEY = ['parent-me']

/** Poll interval for the notification feed — the backend has no push channel. */
export const DUPLICATES_POLL_MS = 60_000

/**
 * The signed-in user's own parent record.
 *
 * `getParentMe()` swallows the 404 a user without a Parent row gets, so `null`
 * means "not a parent yet" rather than an error.
 */
export const useMyParentRecord = (enabled = true) => {
    const query = useQuery({
        queryKey: PARENT_ME_KEY,
        queryFn: () => AuthService.getParentMe(),
        enabled,
        staleTime: 5 * 60_000
    })

    return {
        parent: query.data ?? null,
        parentId: query.data?._id ?? null,
        loading: query.isLoading,
        error: query.error ?? null
    }
}

type ListResult = {
    duplicates: ParentDuplicateRecord[]
    loading: boolean
    fetching: boolean
    error: Error | null
}

/** Every unreviewed pair in the church. Admin-facing. */
export const usePendingDuplicates = (
    options: { enabled?: boolean; refetchInterval?: number } = {}
): ListResult => {
    const query = useQuery({
        queryKey: DUPLICATES_PENDING_KEY,
        enabled: options.enabled ?? true,
        // Stop polling once it fails, so a user who can reach the page but not
        // the endpoint doesn't retry on a loop forever.
        refetchInterval: (q) =>
            q.state.status === 'error' ? false : options.refetchInterval ?? false,
        queryFn: () => ParentDuplicatesService.pending()
    })

    return {
        duplicates: query.data ?? [],
        loading: query.isLoading,
        fetching: query.isFetching,
        error: query.error ?? null
    }
}

/** Every reviewed pair — merged and dismissed alike. Admin-facing. */
export const useProcessedDuplicates = (
    options: { enabled?: boolean } = {}
): ListResult => {
    const query = useQuery({
        queryKey: DUPLICATES_PROCESSED_KEY,
        enabled: options.enabled ?? true,
        queryFn: () => ParentDuplicatesService.processed()
    })

    return {
        duplicates: query.data ?? [],
        loading: query.isLoading,
        fetching: query.isFetching,
        error: query.error ?? null
    }
}

/**
 * The pairs the signed-in parent appears in, on either side.
 *
 * `GET /parent-duplicates/by-parent/:id` is the only listing a plain parent can
 * call without pulling in the whole church, and `includeRejected` is left off so
 * a pair someone already dismissed stops nagging them.
 */
export const useMyDuplicates = (
    options: { enabled?: boolean; refetchInterval?: number } = {}
) => {
    const enabled = options.enabled ?? true
    const { parentId, loading: loadingParent } = useMyParentRecord(enabled)

    const query = useQuery({
        queryKey: [...DUPLICATES_MINE_KEY, parentId],
        enabled: enabled && !!parentId,
        refetchInterval: (q) =>
            q.state.status === 'error' ? false : options.refetchInterval ?? false,
        queryFn: () => ParentDuplicatesService.byParent(parentId as string)
    })

    return {
        duplicates: query.data ?? [],
        parentId,
        // A signed-in user with no Parent row can't have duplicates; report
        // "done, nothing found" rather than a spinner that never resolves.
        loading: loadingParent || (!!parentId && query.isLoading),
        fetching: query.isFetching,
        error: query.error ?? null
    }
}
